import React from 'react';
import IconButtonWhiteBg from '../../../common/IconButtonWhiteBg';
import { AppContext } from '../../../../App';
import { MarkDispatchContext, MarkStateContext } from '../../../../context/MarkContext';
import { Badge, Checkbox, Divider, FormControlLabel, FormGroup, Popover, Box } from '@mui/material';
import { TValidationType } from '../../../../types/validation-types';
import ButtonGlynt from '../../../common/Buttons/ButtonGlynt';
import FilterAltIcon from '@mui/icons-material/FilterAlt';

const IconToggleValidations: React.FC = () => {
  const dispatch = React.useContext<React.Dispatch<any>>(MarkDispatchContext);
  const {
    gridShowValidationErrors,
    showValidations,
    validationsEnabled,
    validationSummaryByExtractionId,
  } = React.useContext(MarkStateContext);
  const { showReviewSpreadsheet } = React.useContext(AppContext);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [localSelections, setLocalSelections] =
    React.useState<TValidationType[]>(gridShowValidationErrors);

  const countErrorsByType = (errorType: TValidationType) => {
    return validationSummaryByExtractionId.reduce(
      (total, validation) => total + (validation.validationErrorsList.includes(errorType) ? 1 : 0),
      0
    );
  };

  const errorDetails: Record<TValidationType, number> = {
    currentcharges: countErrorsByType('currentcharges'),
    amount: countErrorsByType('amount'),
    date: countErrorsByType('date'),
    number: countErrorsByType('number'),
    required: countErrorsByType('required'),
    unknown: countErrorsByType('unknown'),
  };

  const handleToggleValidationsClick = () => {
    const el = document.getElementById('icon-filter-validation-errors');
    if (el) setAnchorEl(el);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCheckboxChange = (type: TValidationType) => {
    setLocalSelections((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleSubmit = () => {
    dispatch({
      type: 'GRID_SHOW_VALIDATION_ERRORS',
      payload: localSelections,
    });
    handleClose();
  };

  const titleText = 'Filter Validation Errors';
  const open = Boolean(anchorEl);

  return (
    <>
      <IconButtonWhiteBg
        disabled={!validationsEnabled || !showValidations || !showReviewSpreadsheet}
        onClick={handleToggleValidationsClick}
        id="icon-filter-validation-errors"
        cypressId="btn-header-filterValidationErrors"
        tooltip={titleText}
      >
        <Badge
          badgeContent={
            gridShowValidationErrors?.length > 0
              ? gridShowValidationErrors.length.toString()
              : undefined
          }
          color="primary"
          sx={{
            '& .MuiBadge-badge': { fontSize: '10px', minWidth: 14, height: 14, right: -4, top: -2 },
          }}
        >
          <FilterAltIcon style={{ color: 'black', fontSize: '14px' }} />
        </Badge>
      </IconButtonWhiteBg>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: { width: '205px' },
        }}
      >
        <FormGroup
          sx={{
            p: 2,
            '& .MuiFormControlLabel-root': { marginBottom: '1px' },
            '& .MuiCheckbox-root': { padding: '4px' },
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={localSelections.length === 6}
                data-cy="checkbox-validations-filter-select-all"
                indeterminate={localSelections.length > 0 && localSelections.length < 6}
                onChange={() => {
                  if (localSelections.length === 6) {
                    setLocalSelections([]);
                  } else {
                    const selectedErrors = Object.keys(errorDetails).filter(
                      (key) => errorDetails[key as TValidationType] > 0
                    ) as TValidationType[];
                    setLocalSelections(selectedErrors);
                  }
                }}
              />
            }
            label="Check All"
            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
          />
          <Divider sx={{ my: 1 }} />

          {errorDetails.amount > 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  data-cy="checkbox-validations-filter-amount"
                  checked={localSelections.includes('amount')}
                  onChange={() => handleCheckboxChange('amount')}
                />
              }
              label={`Amount (${errorDetails.amount})`}
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
          )}
          {errorDetails.currentcharges > 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  data-cy="checkbox-validations-filter-current-charges"
                  checked={localSelections.includes('currentcharges')}
                  onChange={() => handleCheckboxChange('currentcharges')}
                />
              }
              label={`Current Charges (${errorDetails.currentcharges})`}
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
          )}
          {errorDetails.date > 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  data-cy="checkbox-validations-filter-date"
                  checked={localSelections.includes('date')}
                  onChange={() => handleCheckboxChange('date')}
                />
              }
              label={`Date (${errorDetails.date})`}
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
          )}
          {errorDetails.number > 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  data-cy="checkbox-validations-filter-number"
                  checked={localSelections.includes('number')}
                  onChange={() => handleCheckboxChange('number')}
                />
              }
              label={`Number (${errorDetails.number})`}
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
          )}
          {errorDetails.required > 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  data-cy="checkbox-validations-filter-required"
                  checked={localSelections.includes('required')}
                  onChange={() => handleCheckboxChange('required')}
                />
              }
              label={`Required (${errorDetails.required})`}
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
          )}
          {errorDetails.unknown > 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  data-cy="checkbox-validations-filter-unkown"
                  checked={localSelections.includes('unknown')}
                  onChange={() => handleCheckboxChange('unknown')}
                />
              }
              label={`Unknown (${errorDetails.unknown})`}
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
          )}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <ButtonGlynt
              size="xs"
              data-cy="btn-header-filterValidationErrors-clear"
              onClick={() => {
                setLocalSelections([]);
              }}
              variant="outlined"
            >
              Clear
            </ButtonGlynt>
            <ButtonGlynt
              size="xs"
              onClick={handleSubmit}
              variant="outlined"
              data-cy="btn-header-filterValidationErrors-apply"
            >
              Apply
            </ButtonGlynt>
          </Box>
        </FormGroup>
      </Popover>
    </>
  );
};

export default IconToggleValidations;
